/**
 * i18n bootstrap for the admin portal.
 *
 * Scope (proof-of-concept): only the Localization settings section uses
 * translations today (label-level proof that switching language re-renders
 * everywhere it's consumed). Add more namespaces by dropping JSON files in
 * `src/locales/{en,hi}/` and listing them under `ns` below.
 *
 * Language is owned by the existing `useAdminSettings` store
 * (`settings.localization.language`). A small hook subscribes to that and
 * calls `i18n.changeLanguage` whenever it changes — keeps i18n as a side
 * effect of the settings store rather than its own duplicate source of truth.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import enSettings from './infrastructure/locales/en/settings.json';
import hiSettings from './infrastructure/locales/hi/settings.json';

void i18n.use(initReactI18next).init({
  resources: {
    en: { settings: enSettings },
    hi: { settings: hiSettings },
  },
  // Initial language is set from the cached AdminSettings on app boot;
  // `useI18nLanguageSync` keeps it in step thereafter.
  lng: readLanguageFromCache(),
  fallbackLng: 'en',
  ns: ['settings'],
  defaultNS: 'settings',
  interpolation: { escapeValue: false },
  returnNull: false,
});

function readLanguageFromCache(): string {
  try {
    const raw = localStorage.getItem('admin-portal.settings.v1');
    if (!raw) return 'en';
    const parsed = JSON.parse(raw);
    return parsed?.localization?.language ?? 'en';
  } catch {
    return 'en';
  }
}

export default i18n;
